import { createContext, FunctionComponent, PropsWithChildren, useEffect } from 'react';
import { useContext, useState } from 'react';
import { styled } from 'styled-components';
import { Swiper, SwiperClass } from 'swiper/react';
import { Zoom, Navigation, Keyboard, Virtual } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/virtual';
import 'swiper/css/zoom';
import 'swiper/css/keyboard';
import { useTouchScreen } from '../../../hooks/useTouchScreen';
import { IMediaGalleryModal } from '../MediaGalleryModalContext/MediaGalleryModalContext';
import { MediaItemV2 } from '../../../app-kit/components/Image/types';
import {
  ImageEventData,
  trackFullScreenCarouselView,
} from '../../../analytics/event-tracking/trackFullScreenCarouselView';
import { useRef } from 'react';

export interface MediaGalleryCarouselContext {
  swiper: SwiperClass | null;
  isBeginning: boolean;
  isEnd: boolean;
  isZoomed: boolean;
  isTouchScreen: boolean;
  currentSlide: number;
  isUIHidden: boolean;
}

export const MediaGalleryCarouselContext = createContext<MediaGalleryCarouselContext | undefined>(
  undefined,
);

export const useMediaGalleryCarousel = (): MediaGalleryCarouselContext => {
  const context = useContext(MediaGalleryCarouselContext);
  if (context === undefined) {
    throw new Error(
      'MediaGalleryCarouselContext must be used within a MediaGalleryCarouselContext.Provider',
    );
  }
  return context;
};

const SwiperCarousel = styled(Swiper)`
  height: 100%;
  width: 100%;
`;

export const mergeImageTrackingEventDataWithUserAction = (
  eventData: ImageEventData,
  userAction: string,
): ImageEventData => {
  return {
    ...eventData,
    data: {
      ...eventData.data,
      user_action: userAction,
    },
  };
};

const useFullScreenCarouselTracking = (
  eventData: ImageEventData,
  openAction: IMediaGalleryModal['openAction'],
) => {
  const [shouldTrackImageChange, setShouldTrackImageChange] = useState(false);

  useEffect(() => {
    trackFullScreenCarouselView(mergeImageTrackingEventDataWithUserAction(eventData, openAction));
    setShouldTrackImageChange(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!shouldTrackImageChange) return;

    trackFullScreenCarouselView(
      mergeImageTrackingEventDataWithUserAction(eventData, 'MediaGallery:ImageChange'),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData]);
};

const DOUBLE_CLICK_TIMEOUT_MS = 300;
export const MediaGalleryCarouselContextProvider: FunctionComponent<
  PropsWithChildren<{
    initialSlideIndex: number;
    galleryItems: MediaItemV2[];
    openAction: IMediaGalleryModal['openAction'];
  }>
> = ({ children, initialSlideIndex, galleryItems, openAction }) => {
  const doubleClickTimeout = useRef<NodeJS.Timeout | null>(null);
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [isBeginning, setIsBeginning] = useState<boolean>(true);
  const [isEnd, setIsEnd] = useState<boolean>(false);
  const [isZoomed, setIsZoomed] = useState<boolean>(false);
  const { isSupported: isTouchScreen } = useTouchScreen();
  const [currentSlide, setCurrentSlide] = useState<number>(initialSlideIndex);
  const [isUIHidden, setIsUIHidden] = useState<boolean>(false);

  const handleClick = () => {
    //When single click happens, we set a timeout to wait and see if a double click happens
    if (doubleClickTimeout.current) clearTimeout(doubleClickTimeout.current);
    doubleClickTimeout.current = setTimeout(() => {
      setIsUIHidden(!isUIHidden);
    }, DOUBLE_CLICK_TIMEOUT_MS);
  };

  const handleDoubleClick = () => {
    //When double click happens, we clear the single click timeout to prevent both firing
    if (doubleClickTimeout.current) clearTimeout(doubleClickTimeout.current);
  };

  useFullScreenCarouselTracking(
    galleryItems[currentSlide].tracking.fullScreenCarouselView,
    openAction,
  );

  useEffect(() => {
    if (!swiper) return;

    const onSlideChange = () => {
      //We need to manually set these as swiper doesn't re-render React components on slide change
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
      setCurrentSlide(swiper.activeIndex);
    };

    onSlideChange(); //Set on swiper init

    swiper.on('slideChange', onSlideChange);
    return () => {
      swiper.off('slideChange', onSlideChange);
    };
  }, [swiper]);

  return (
    <MediaGalleryCarouselContext.Provider
      value={{ swiper, isBeginning, isEnd, isZoomed, isTouchScreen, currentSlide, isUIHidden }}
    >
      <SwiperCarousel
        navigation={false}
        zoom
        keyboard
        virtual
        initialSlide={currentSlide}
        modules={[Zoom, Navigation, Keyboard, Virtual]}
        onSwiper={setSwiper}
        onZoomChange={(swiper) => setIsZoomed(swiper.zoom.scale === 1)}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      >
        {children}
      </SwiperCarousel>
    </MediaGalleryCarouselContext.Provider>
  );
};
